import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

const TOP_FALLBACK = 56; // h-14
const BOTTOM_FALLBACK = 0; // we don't want extra gap if there is no tab bar

const getElementHeight = (element, fallback = 0) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    if (rect.height > 0) return rect.height;
    if (typeof window !== "undefined") {
        const style = window.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden") return 0;
    }
    return fallback;
};

const getOffsets = () => {
    const topbar = document.getElementById("topbar");
    const tabbar = document.querySelector(".tab-bar");
    return {
        top: getElementHeight(topbar, TOP_FALLBACK),
        bottom: getElementHeight(tabbar, BOTTOM_FALLBACK),
    };
};

const ModalSafeArea = ({ open, onClose, children, backdropOpacity = 0.45, className = "", style = {} }) => {
    const [offsets, setOffsets] = useState(() => getOffsets());
    const rafRef = useRef(null);
    const handleResize = useCallback(() => {
        setOffsets(getOffsets());
    }, []);

    useLayoutEffect(() => {
        const scheduleResize = () => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                handleResize();
            });
        };

        window.addEventListener("resize", handleResize);

        let topbar = document.getElementById("topbar");
        let tabbar = document.querySelector(".tab-bar");
        let topbarObserver = null;
        let tabbarObserver = null;

        if (typeof ResizeObserver !== "undefined") {
            if (topbar) {
                topbarObserver = new ResizeObserver(handleResize);
                topbarObserver.observe(topbar);
            }
            if (tabbar) {
                tabbarObserver = new ResizeObserver(handleResize);
                tabbarObserver.observe(tabbar);
            }
        }

        let mutationObserver = null;
        if (typeof MutationObserver !== "undefined") {
            mutationObserver = new MutationObserver(() => {
                scheduleResize();
                const nextTopbar = document.getElementById("topbar");
                if (nextTopbar !== topbar) {
                    if (topbarObserver) topbarObserver.disconnect();
                    topbar = nextTopbar;
                    if (topbar && typeof ResizeObserver !== "undefined") {
                        topbarObserver = new ResizeObserver(handleResize);
                        topbarObserver.observe(topbar);
                    } else {
                        topbarObserver = null;
                    }
                }
                const nextTabbar = document.querySelector(".tab-bar");
                if (nextTabbar !== tabbar) {
                    if (tabbarObserver) tabbarObserver.disconnect();
                    tabbar = nextTabbar;
                    if (tabbar && typeof ResizeObserver !== "undefined") {
                        tabbarObserver = new ResizeObserver(handleResize);
                        tabbarObserver.observe(tabbar);
                    } else {
                        tabbarObserver = null;
                    }
                }
            });
            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["class", "style", "id"],
            });
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            if (topbarObserver) topbarObserver.disconnect();
            if (tabbarObserver) tabbarObserver.disconnect();
            if (mutationObserver) mutationObserver.disconnect();
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, [handleResize]);

    useLayoutEffect(() => {
        if (!open) return;
        handleResize();
        const t1 = setTimeout(handleResize, 200);
        const t2 = setTimeout(handleResize, 800);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [open, handleResize]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-x-0 flex items-center justify-center px-4"
            style={{
                top: offsets.top,
                bottom: offsets.bottom,
                zIndex: 350,
                pointerEvents: "auto",
                ...style,
            }}
        >
            <div
                className="absolute inset-0 rounded"
                style={{ background: `rgba(0,0,0,${backdropOpacity})` }}
                onClick={onClose}
            ></div>
            <div className={`relative w-full max-w-md ${className}`}>{children}</div>
        </div>
    );
};

export default ModalSafeArea;
